<template>
  <div class="customer-contact-card">
    <!-- Loading State -->
    <div v-if="isLoading" class="customer-contact-card__skeleton">
      <div class="customer-contact-content">
        <Skeleton height="1.5rem" width="12rem" class="mb-3" />
        <div class="customer-contact-details">
          <div v-for="i in 3" :key="i" class="contact-item">
            <Skeleton height="1rem" width="5rem" />
            <Skeleton height="1rem" width="10rem" />
          </div>
        </div>
      </div>
    </div>

    <!-- Content State -->
    <div v-else class="customer-contact-content">
      <h2 class="customer-contact-title">Kontaktinformasjon til kunde</h2>

      <!-- Contact Details -->
      <div v-if="hasContactData" class="customer-contact-details">
        <div
          v-for="(detail, index) in contactDetails"
          :key="index"
          :class="['contact-item', { 'contact-item--empty': !detail.value }]"
        >
          <span class="contact-item__label">{{ detail.label }}</span>
          <span class="contact-item__value">{{ detail.value || "—" }}</span>
        </div>
      </div>

      <!-- No Data Fallback -->
      <div v-else class="customer-contact-empty">
        <div class="empty-state">
          <div class="empty-state__icon">
            <i class="pi pi-user" />
          </div>
          <p class="empty-state__message">
            Ingen kundeinformasjon tilgjengelig
          </p>
          <p class="empty-state__subtitle">
            Kontaktinformasjon vil vises når den er tilgjengelig
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * CustomerContactInfo Component
 *
 * Displays customer contact information with loading states and empty state fallback.
 * Features:
 * - Loading skeleton while data is being fetched
 * - Graceful handling of missing or null data
 * - Empty state with clear messaging
 * - Clean, accessible markup structure
 */
import { computed } from "vue";
import Skeleton from "primevue/skeleton";

// ===== TYPES AND INTERFACES =====
interface CustomerDetails {
  displayName: string | null;
  email: string | null;
  mobilePhone: string | null;
}

interface ContactDetail {
  label: string;
  value: string;
  type: "name" | "email" | "phone";
}

interface Props {
  customerDetails: CustomerDetails | null;
  isLoading?: boolean;
}

// ===== PROPS AND DEFAULTS =====
const props = withDefaults(defineProps<Props>(), {
  customerDetails: null,
  isLoading: false,
});

// ===== COMPUTED PROPERTIES =====

/**
 * Computed contact details with proper type information
 * Maps customer data to display format with fallback handling
 */
const contactDetails = computed((): ContactDetail[] => [
  {
    label: "Navn",
    value: props.customerDetails?.displayName?.trim() || "",
    type: "name",
  },
  {
    label: "E-post",
    value: props.customerDetails?.email?.trim() || "",
    type: "email",
  },
  {
    label: "Telefon",
    value: props.customerDetails?.mobilePhone?.trim() || "",
    type: "phone",
  },
]);

/**
 * Determines if we have any meaningful contact data to display
 * Returns true if at least one contact field has data
 */
const hasContactData = computed((): boolean => {
  if (props.customerDetails === null) return false;

  return contactDetails.value.some((detail) => detail.value.length > 0);
});

/**
 * Determines if customer data exists but is empty
 * Used for showing appropriate messaging
 */
const hasEmptyCustomerData = computed((): boolean => {
  return props.customerDetails === null;
});

// ===== EXPOSE FOR TESTING =====
defineExpose({
  contactDetails,
  hasContactData,
  hasEmptyCustomerData,
});
</script>

<style scoped lang="scss">
// ===== DESIGN TOKENS =====
$background: $color-white;
$padding-vertical: 24px;
$padding-horizontal: 32px;
$border-radius: 4px;
$box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;

// Typography
$title-color: #000;
$title-font-size: $font-size-base;
$text-color: #000;
$text-font-size: $font-size-sm;
$text-margin: 16px;

// Layout
$contact-item-gap: 10px;
$min-height: 180px;

// ===== COMPONENT STYLES =====
.customer-contact-card {
  display: flex;
  flex-direction: column;
  border-radius: $border-radius;
  background: $background;
  margin-left: 10px;
  width: 100%;
  min-height: $min-height;
  box-shadow: $box-shadow;
}

.customer-contact-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: $padding-vertical $padding-horizontal;
  width: 100%;
  flex: 1;
}

.customer-contact-card__skeleton {
  width: 100%;
  padding: $padding-vertical $padding-horizontal;
}

.customer-contact-title {
  color: $title-color;
  font-size: $title-font-size;
  font-weight: 600;
  text-align: left;
  margin: 0 0 $text-margin 0;
}

.customer-contact-details {
  font-size: $text-font-size;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding-bottom: $contact-item-gap;
  font-weight: 600;
  font-size: $text-font-size;

  &--empty {
    opacity: 0.6;
  }

  &__label {
    font-weight: 400;
    color: $text-color;
    min-width: 5rem;
  }

  &__value {
    font-weight: 600;
    color: $text-color;
    text-align: right;
    word-break: break-word;
  }
}

// ===== EMPTY STATE STYLES =====
.customer-contact-empty {
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 120px;
  width: 100%;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  opacity: 0.7;

  &__icon {
    font-size: 2rem;
    color: #666;
    margin-bottom: 8px;

    .pi {
      opacity: 0.5;
    }
  }

  &__message {
    font-size: $text-font-size;
    font-weight: 600;
    color: $text-color;
    margin: 0;
  }

  &__subtitle {
    font-size: calc(#{$text-font-size} - 2px);
    font-weight: 400;
    color: #666;
    margin: 0;
    line-height: 1.4;
  }
}
</style>
